import FormControlLabel from '@mui/material/FormControlLabel'
import { styled } from '@mui/material/styles'
import Switch, { SwitchProps, SwitchPropsColorOverrides } from '@mui/material/Switch'
import { OverridableStringUnion } from '@mui/types'
import React from 'react'
import './index.css'

interface SwitchComponentProps extends Omit<SwitchProps, 'color'> {
  disable?: boolean
  isChecked: boolean
  children?: React.ReactNode
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  color?: OverridableStringUnion<
    'primary' | 'secondary' | 'default' | 'error' | 'info' | 'success' | 'warning',
    SwitchPropsColorOverrides
  >
  placement?: 'start' | 'end' | 'top' | 'bottom'
}

const StyledSwitch = styled(Switch, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size: SwitchProps['size'] }>(({ theme, size }) => {
  const isLarge = size === 'medium'

  return {
    width: isLarge ? 48 : 36,
    height: isLarge ? 26 : 20,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: isLarge ? 3 : 2,
      '&.Mui-disabled': {
        '& .MuiSwitch-thumb': {
          backgroundColor: 'transparent',
          border: `1px solid #fff`,
          boxSizing: 'border-box',
        },
        '& + .MuiSwitch-track': {
          opacity: 0.5,
          backgroundColor: theme.palette.grey[400],
        },
      },
      '&.Mui-checked': {
        transform: isLarge ? 'translateX(22px)' : 'translateX(16px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          opacity: 1,
          backgroundColor: theme.palette.secondary.main,
          borderRadius: isLarge ? 13 : 10, // Half of the height for pill shape
        },
      },
    },
    '& .MuiSwitch-thumb': {
      width: isLarge ? 20 : 16,
      height: isLarge ? 20 : 16,
      boxShadow: 'none',
    },
    '& .MuiSwitch-track': {
      borderRadius: isLarge ? 13 : 10,
      backgroundColor: theme.palette.grey[500],
      opacity: 1,
      transition: theme.transitions.create(['background-color'], {
        duration: 500,
      }),
    },
  }
})

const SwitchComponent = (props: SwitchComponentProps): JSX.Element => {
  const { disable, isChecked, children, onChange, color, placement, size = 'medium' } = props
  return (
    <FormControlLabel
      control={<StyledSwitch disabled={disable} checked={isChecked} onChange={onChange} color={color} size={size} />}
      label={
        children && (
          <span
            className='text-gray-400 text-sm font-medium py-2'
            style={{
              flex: 1,
              margin: 0,
              padding: 8,
              display: 'block',
              alignSelf: 'center',
              textAlign: 'left',
              fontSize: size === 'small' ? '0.8125rem' : '1rem',
            }}
          >
            {children}
          </span>
        )
      }
      labelPlacement={placement ?? 'end'}
      sx={{ margin: 0 }} // Removed default margins
    />
  )
}

export default SwitchComponent
